/**
 * Document parser module
 * Handles text parsing and word-level segmentation (regex based, positions
 * match the original text exactly) with language detection
 */

import LanguageManager, { type LanguageOption } from './language-manager';
import type { LanguageCode } from './language-constants';
import { logger } from './logger';

// Type definitions
export interface DocumentParserOptions {
  language?: LanguageCode;
  autoDetectLanguage?: boolean;
}

export interface ParsedWord {
  text: string;
  start: number;
  end: number;
  index: number;
  length: number;
  type: string;
  lemma: string;
}

export interface ParsedSentence {
  text: string;
  start: number;
  end: number;
  index: number;
  wordCount: number;
}

export interface ParsedParagraph {
  text: string;
  start: number;
  end: number;
  index: number;
  wordCount: number;
}

export interface ParseDocumentOptions {
  language?: LanguageCode;
  detectionOptions?: Record<string, unknown>;
}

export interface ParsedDocument {
  content: string;
  words: ParsedWord[];
  sentences: ParsedSentence[];
  paragraphs: ParsedParagraph[];
  metadata: {
    wordCount: number;
    sentenceCount: number;
    paragraphCount: number;
    characterCount: number;
    parseTime: number;
    language: LanguageCode;
    detectedLanguage: LanguageCode;
    languageName: string;
  };
}

class DocumentParser {
  private languageManager: LanguageManager;
  private currentLanguage: LanguageCode;
  private autoDetectLanguage: boolean;

  constructor(options: DocumentParserOptions = {}) {
    this.languageManager = new LanguageManager();
    this.currentLanguage = this.languageManager.validateLanguageCode(
      options.language || this.languageManager.getDefaultLanguage()
    ) as LanguageCode;
    this.autoDetectLanguage = options.autoDetectLanguage !== false;
  }

  /**
   * Parse document content into structured data
   * @param content - Raw document content
   * @param options - Parsing options
   * @returns Parsed document structure
   */
  async parseDocument(
    content: string,
    options: ParseDocumentOptions = {}
  ): Promise<ParsedDocument> {
    const startTime = performance.now();

    if (!content || typeof content !== 'string') {
      throw new Error('Invalid document content');
    }

    const cleanContent = this.cleanContent(content);
    let detectedLanguage = this.currentLanguage;

    // Auto-detect language if enabled and no language specified
    if (this.autoDetectLanguage && !options.language) {
      try {
        detectedLanguage = (await this.languageManager.detectLanguage(
          cleanContent,
          options.detectionOptions
        )) as LanguageCode;
        logger.debug(`Detected language: ${detectedLanguage}`);
      } catch (error) {
        logger.warn('Language detection failed:', error);
      }
    }

    // Use provided language or detected language
    const targetLanguage = options.language || detectedLanguage;

    // Update current language if different
    if (targetLanguage !== this.currentLanguage) {
      this.setLanguage(targetLanguage);
    }

    const words = await this.extractWords(cleanContent, targetLanguage);
    const sentences = await this.extractSentences(cleanContent, targetLanguage);
    const paragraphs = this.extractParagraphs(cleanContent);

    const result: ParsedDocument = {
      content: cleanContent,
      words: words,
      sentences: sentences,
      paragraphs: paragraphs,
      metadata: {
        wordCount: words.length,
        sentenceCount: sentences.length,
        paragraphCount: paragraphs.length,
        characterCount: cleanContent.length,
        parseTime: performance.now() - startTime,
        language: targetLanguage,
        detectedLanguage: detectedLanguage,
        languageName:
          this.languageManager.getLanguageDisplayName(targetLanguage),
      },
    };

    return result;
  }

  /**
   * Clean and normalize document content
   * Only normalizes line endings - does NOT modify whitespace since docling returns clean text
   * @param content - Raw content
   * @returns Cleaned content
   */
  cleanContent(content: string): string {
    return content
      .replace(/\r\n/g, '\n') // Normalize line endings
      .replace(/\r/g, '\n') // Handle old Mac line endings
      .trim();
  }

  /**
   * Extract individual words with position information using regex
   * Regex based, not a tokenizer: tokenizers modify the text (expand contractions etc.),
   * which breaks entity highlighting that relies on exact character positions.
   * @param content - Document content
   * @param language - Language code (unused but kept for API compatibility)
   * @returns Array of word objects
   */
  async extractWords(
    content: string,
    _language?: LanguageCode
  ): Promise<ParsedWord[]> {
    const words: ParsedWord[] = [];

    // Always use regex-based extraction to preserve exact text
    // This ensures positions match exactly with the original document text
    this.extractWordsWithRegex(content, words);

    return words;
  }

  /**
   * Extract words using regex fallback
   * @param content - Document content
   * @param words - Words array to populate
   */
  private extractWordsWithRegex(content: string, words: ParsedWord[]): void {
    // Use matchAll instead of exec loop to avoid ReDoS vulnerability
    // Match word characters including hyphens within words (e.g., "more-text")
    const wordRegex = /\b[\w-]+\b/g;
    const matches = content.matchAll(wordRegex);

    for (const match of matches) {
      words.push({
        text: match[0],
        start: match.index!,
        end: match.index! + match[0].length,
        index: words.length,
        length: match[0].length,
        type: 'word',
        lemma: match[0],
      });
    }
  }

  /**
   * Extract sentences with position information
   * @param content - Document content
   * @param _language - Language code (unused, kept for API compatibility)
   * @returns Array of sentence objects
   */
  async extractSentences(
    content: string,
    _language: LanguageCode | null = null
  ): Promise<ParsedSentence[]> {
    const sentences: ParsedSentence[] = [];
    // matchAll over a linear pattern (no ReDoS); sentences end with . ! or ?
    const sentenceRegex = /[^.!?]+[.!?]+/g;
    for (const match of content.matchAll(sentenceRegex)) {
      const text = match[0].trim();
      if (text.length > 0) {
        sentences.push({
          text: text,
          start: match.index!,
          end: match.index! + match[0].length,
          index: sentences.length,
          wordCount: this.countWordsInText(text),
        });
      }
    }
    return sentences;
  }

  /**
   * Extract paragraphs with position information
   * @param content - Document content
   * @returns Array of paragraph objects
   */
  extractParagraphs(content: string): ParsedParagraph[] {
    const paragraphs: ParsedParagraph[] = [];
    const lines = content.split('\n');
    let currentParagraph = '';
    let startIndex = 0;
    let currentIndex = 0;

    for (let i = 0; i < lines.length; i++) {
      const currentLine = lines[i];
      if (currentLine === undefined) continue;

      const line = currentLine.trim();

      if (line.length === 0) {
        // Empty line - end current paragraph
        if (currentParagraph.trim().length > 0) {
          paragraphs.push({
            text: currentParagraph.trim(),
            start: startIndex,
            end: currentIndex,
            index: paragraphs.length,
            wordCount: this.countWordsInText(currentParagraph),
          });
        }
        currentParagraph = '';
        startIndex = currentIndex + currentLine.length + 1; // +1 for newline
      } else {
        // Add line to current paragraph
        if (currentParagraph.length > 0) {
          currentParagraph += ' ';
        }
        currentParagraph += line;
      }

      currentIndex += currentLine.length + 1; // +1 for newline
    }

    // Handle last paragraph
    if (currentParagraph.trim().length > 0) {
      paragraphs.push({
        text: currentParagraph.trim(),
        start: startIndex,
        end: currentIndex,
        index: paragraphs.length,
        wordCount: this.countWordsInText(currentParagraph),
      });
    }

    return paragraphs;
  }

  /**
   * Count words in a text string
   * @param text - Text to count words in
   * @returns Word count
   */
  countWordsInText(text: string): number {
    return text
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
  }

  /**
   * Set current language for parsing
   * @param langCode - Language code
   */
  setLanguage(langCode: LanguageCode): void {
    const validatedLang = this.languageManager.validateLanguageCode(
      langCode
    ) as LanguageCode;
    this.currentLanguage = validatedLang;
  }

  /**
   * Get current language
   * @returns Current language code
   */
  getCurrentLanguage(): LanguageCode {
    return this.currentLanguage;
  }

  /**
   * Get supported languages
   * @returns Supported languages configuration
   */
  getSupportedLanguages(): Record<string, unknown> {
    return this.languageManager.getSupportedLanguages();
  }

  /**
   * Get language options for UI components
   * @param includeNativeNames - Include native names
   * @returns Language options
   */
  getLanguageOptions(includeNativeNames: boolean = true): LanguageOption[] {
    return this.languageManager.getLanguageOptions(includeNativeNames);
  }

  /**
   * Detect language of text
   * @param text - Text to analyze
   * @param options - Detection options
   * @returns Detected language code
   */
  async detectLanguage(
    text: string,
    options: Record<string, unknown> = {}
  ): Promise<LanguageCode> {
    return this.languageManager.detectLanguage(
      text,
      options
    ) as Promise<LanguageCode>;
  }

  /**
   * Enable/disable automatic language detection
   * @param enabled - Whether to enable auto-detection
   */
  setAutoDetectLanguage(enabled: boolean): void {
    this.autoDetectLanguage = !!enabled;
  }

  /**
   * Get language display name
   * @param langCode - Language code
   * @param native - Whether to return native name
   * @returns Language display name
   */
  getLanguageDisplayName(
    langCode: LanguageCode | null = null,
    native: boolean = false
  ): string {
    const language = langCode || this.currentLanguage;
    return this.languageManager.getLanguageDisplayName(language, native);
  }
}

// Export to window for debugging (dev only)
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.DocumentParser = DocumentParser;
}

export default DocumentParser;
